import * as React from 'react';

import MapFrame, { MapProps } from './Map';
import { MapEvent } from '~/config/map';

export interface MapPostEventWrapperRef {
  sendEvent: (type: MapEvent, payload?: any) => void;
  listenToEvent: (type: MapEvent, cb: (payload: any) => void) => void;
  removeEventListener: (cb: (payload: any) => void) => void;
}

const EVENTS_REQUIRE_MAP_INIT = [MapEvent.GoToMarker, MapEvent.SetOutdoorPolygon, MapEvent.ZoneDrawZonesChanged];

export interface MapPostEventWrapperProps extends MapProps {
  onMapLoaded?: (map: MapPostEventWrapperRef) => void;
}

const MapPostEventWrapper = React.forwardRef<MapPostEventWrapperRef, MapPostEventWrapperProps>((props, ref) => {
  const { onMapLoaded, ...mapProps } = props;
  const mapRef = React.useRef<HTMLIFrameElement>(null);
  const listeners = React.useRef<any[]>([]);
  const mapping = React.useRef<any[]>([]);
  const appInitialisedRef = React.useRef(false);
  const mapInitialisedRef = React.useRef(false);
  const queueRef = React.useRef<[MapEvent, any][]>([]);

  const sendEvent = React.useCallback((type: MapEvent, payload?: any) => {
    if (!appInitialisedRef.current) {
      queueRef.current.push([type, payload]);
      return;
    }
    if (!mapInitialisedRef.current && EVENTS_REQUIRE_MAP_INIT.includes(type)) {
      queueRef.current.push([type, payload]);
      return;
    }
    try {
      mapRef.current?.contentWindow?.postMessage(JSON.stringify({ type, payload }), '*');
    } catch {
      // ignore postMessage failures
    }
  }, []);

  const flushQueue = React.useCallback(() => {
    const queue = [...queueRef.current];
    queueRef.current = [];
    queue.forEach(([type, payload]) => {
      sendEvent(type, payload);
    });
  }, [sendEvent]);

  const handleMessageReceive = React.useCallback((event: MessageEvent<any>) => {
    if (typeof event.data !== 'string') {
      return;
    }
    const dataParsed = JSON.parse(event.data);
    listeners.current.forEach((listener) => {
      listener(dataParsed.type, dataParsed.payload);
    });
  }, []);

  const listenToEvent = React.useCallback((type: MapEvent, cb: (payload: any) => void) => {
    if (mapping.current.includes(cb)) {
      return;
    }
    const listener = (eventType: MapEvent, payload: any) => {
      if (eventType === type) {
        cb(payload);
      }
    };
    mapping.current.push(cb);
    listeners.current.push(listener);
  }, []);

  const removeEventListener = React.useCallback((cb: (payload: any) => void) => {
    const index = mapping.current.findIndex((m) => m === cb);
    if (index === -1) {
      return;
    }
    listeners.current.splice(index, 1);
    mapping.current.splice(index, 1);
  }, []);

  React.useImperativeHandle(ref, () => ({
    sendEvent,
    listenToEvent,
    removeEventListener,
  }));

  const appInitialisedHandler = React.useCallback(() => {
    appInitialisedRef.current = true;
    flushQueue();
  }, []);

  const mapInitialisedHandler = React.useCallback(() => {
    mapInitialisedRef.current = true;
    flushQueue();
    onMapLoaded?.({ sendEvent, listenToEvent, removeEventListener });
  }, [onMapLoaded, sendEvent, listenToEvent, removeEventListener]);

  React.useEffect(() => {
    listenToEvent(MapEvent.AppInitialised, appInitialisedHandler);
    listenToEvent(MapEvent.MapInitialised, mapInitialisedHandler);
    window.addEventListener('message', handleMessageReceive);

    return () => {
      window.removeEventListener('message', handleMessageReceive);
    };
  }, [listenToEvent, handleMessageReceive, flushQueue]);

  return <MapFrame ref={mapRef} {...mapProps} />;
});

export default MapPostEventWrapper;
